/**
 * @file ProductFormScreen.tsx
 *
 * Routed wrapper around {@link ProductForm} for the `/products/new`,
 * `/products/:id/edit`, and `/products/:id/duplicate` routes. Resolves the seed
 * product for edit/duplicate via the store (cache-first, then fetch-by-id so a
 * direct link or refresh still works), shows loading/error states, and owns
 * persistence: it calls the store's create/update, toasts the save outcome, and
 * navigates back to the list on success. Related-product candidates come from the
 * loaded product list.
 */

import { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Loader } from '@group.one/react-gravity';
import { FormattedMessage, useIntl } from 'react-intl';
import { adminMessages } from '../../i18n/messages';
import { notifyError, notifySuccess } from '../../feedback/toast';
import { useProductsStore } from '../../stores/products.store';
import { useSettingsStore } from '../../stores/settings.store';
import type { ShopProductPayload, ShopProductVariant } from '../../api/shop-types';
import { EmptyState } from '../../components/EmptyState';
import { ProductForm, type ProductFormMode } from './ProductForm';

interface ProductFormScreenProps {
  mode: ProductFormMode;
}

/**
 * Maps a known machine-readable save-failure code (from {@link WcApiError.code}, captured on
 * the store as `formErrorCode`) to a specific message descriptor. An unknown or absent code
 * falls back to the generic save error, so a new server code degrades gracefully rather than
 * surfacing a raw string.
 */
const SAVE_ERROR_MESSAGE_BY_CODE: Record<
  string,
  (typeof adminMessages)[keyof typeof adminMessages]
> = {
  resource_stale: adminMessages.formSaveErrorStale,
  product_sku_conflict: adminMessages.formSaveErrorSkuConflict,
  product_slug_conflict: adminMessages.formSaveErrorSlugConflict,
  product_uniform_price_refused: adminMessages.formSaveErrorUniformPriceRefused,
  product_no_enabled_variant: adminMessages.formSaveErrorNoEnabledVariant,
  product_too_many_variants: adminMessages.formSaveErrorTooManyVariants,
  product_option_duplicate: adminMessages.formSaveErrorOptionDuplicate,
  product_variant_image_invalid: adminMessages.formSaveErrorVariantImageInvalid,
  product_price_invalid: adminMessages.formSaveErrorPriceInvalid,
  product_variant_override_invalid: adminMessages.formSaveErrorVariantOverrideInvalid,
};

/** Resolves the save-failure code to its message descriptor, defaulting to the generic error. */
function saveErrorMessageForCode(
  code: string | null,
): (typeof adminMessages)[keyof typeof adminMessages] {
  return (code && SAVE_ERROR_MESSAGE_BY_CODE[code]) || adminMessages.formSaveError;
}

/**
 * Renders the routed product form for the given mode.
 *
 * @param props - The form mode (create/edit/duplicate).
 *
 * @returns The product form screen (with loading/error fallbacks).
 */
export function ProductFormScreen({ mode }: ProductFormScreenProps) {
  const intl = useIntl();
  const navigate = useNavigate();
  const { id } = useParams();

  const products = useProductsStore((s) => s.products);
  const categories = useProductsStore((s) => s.categories);
  const currentProduct = useProductsStore((s) => s.current);
  const formLoading = useProductsStore((s) => s.formLoading);
  const formError = useProductsStore((s) => s.formError);
  const loadOne = useProductsStore((s) => s.loadOne);
  const fetchProducts = useProductsStore((s) => s.fetchProducts);
  const fetchCategories = useProductsStore((s) => s.fetchCategories);
  const saveProductForm = useProductsStore((s) => s.saveProductForm);
  const resetForm = useProductsStore((s) => s.resetForm);

  const currencySymbol = useSettingsStore((s) => s.currencySymbol);

  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState<string | undefined>(undefined);

  useEffect(() => {
    fetchCategories();
    if (products.length === 0) {
      fetchProducts();
    }
    if (mode === 'create') {
      resetForm();
    } else if (id !== undefined) {
      loadOne(id);
    }
    return () => resetForm();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mode, id]);

  const handleSubmit = async (
    payload: ShopProductPayload,
    variantRows: ShopProductVariant[],
  ): Promise<void> => {
    setSaving(true);
    setSaveError(undefined);
    const result = await saveProductForm({ mode, id, payload, variantRows });
    setSaving(false);
    if (!result) {
      // Read the freshly-set code from the store (the failing save `set()`s it after this
      // closure was created, so a subscribed selector would be stale here).
      const code = useProductsStore.getState().formErrorCode;
      setSaveError(intl.formatMessage(saveErrorMessageForCode(code)));
      notifyError(intl.formatMessage(adminMessages.toastError));
      return;
    }
    notifySuccess(intl.formatMessage(adminMessages.toastSaved));
    navigate('/products');
  };

  const goBack = (): void => {
    navigate('/products');
  };

  if (mode !== 'create') {
    // The store's `formError` covers both a failed initial load and a failed save. Only a
    // load failure (no product resolved) belongs on the full-page error state; a save failure
    // keeps the loaded form so its specific inline `saveError` message stays visible.
    if (formError && !currentProduct) {
      return (
        <EmptyState
          title={intl.formatMessage(adminMessages.formLoadErrorTitle)}
          description={intl.formatMessage(adminMessages.formLoadErrorDescription)}
          action={{
            label: intl.formatMessage(adminMessages.productsTitle),
            onClick: goBack,
          }}
        />
      );
    }
    if (formLoading || !currentProduct || currentProduct.id !== id) {
      return (
        <div className="gv-flex gv-items-center gv-justify-center gv-h-full gv-w-full">
          <Loader>
            <FormattedMessage {...adminMessages.formLoading} />
          </Loader>
        </div>
      );
    }
  }

  return (
    <ProductForm
      mode={mode}
      product={mode === 'create' ? undefined : (currentProduct ?? undefined)}
      duplicate={mode === 'duplicate'}
      categories={categories}
      relatedCandidates={products.filter((p) => p.id !== id)}
      currencySymbol={currencySymbol}
      saving={saving}
      saveError={saveError}
      onSubmit={handleSubmit}
      onCancel={goBack}
    />
  );
}
